import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import type { OfferAttachment } from '../../../types';
import { offerAttachmentFileSrc } from '../utils/offerFirstPageImages';

type Props = {
  leadRouteId: string;
  attachments: OfferAttachment[];
  readOnly?: boolean;
  onRemove?: (attachment: OfferAttachment) => void | Promise<void>;
  onReplace?: (attachment: OfferAttachment) => void;
};

const iconBtnClass =
  'inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-app-hover hover:text-app-text';

export function FirstPageAttachmentImageGrid({
  leadRouteId,
  attachments,
  readOnly = false,
  onRemove,
  onReplace,
}: Props) {
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const run = async (attachment: OfferAttachment, action: () => Promise<void>) => {
    setBusyId(attachment.id);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Aktion fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  if (attachments.length === 0) return null;
  return (
    <div>
      <div className="grid grid-cols-3 gap-3">
        {attachments.map((attachment) => {
          const busy = busyId === attachment.id;
          const editable = !readOnly && (onRemove || onReplace);
          return (
            <div key={attachment.id} className="group relative">
              <img
                src={offerAttachmentFileSrc(leadRouteId, attachment.object_path)}
                alt={attachment.filename}
                className="aspect-[4/3] w-full bg-slate-100 object-contain"
              />
              {busy ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                  <LoadingSpinner size="sm" />
                </div>
              ) : null}
              {editable && !busy ? (
                <div className="absolute right-1 top-1 flex gap-0.5 rounded-md bg-white/90 p-0.5 shadow-sm opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                  {onReplace ? (
                    <button
                      type="button"
                      className={iconBtnClass}
                      title="Bilder bearbeiten"
                      aria-label="Bilder dieser Reihe bearbeiten"
                      onClick={() => onReplace(attachment)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                  {onRemove ? (
                    <button
                      type="button"
                      className={`${iconBtnClass} hover:bg-rose-50 hover:text-rose-600`}
                      title="Bild löschen"
                      aria-label={`${attachment.filename} löschen`}
                      onClick={() => void run(attachment, async () => onRemove(attachment))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {error ? (
        <p className="mt-2 text-[9.5pt] text-red-700">{error}</p>
      ) : null}
    </div>
  );
}
